import {
  ArticleBullets,
  ArticleImage,
  ArticleLayout,
  ArticleParagraph,
  ArticleSection,
  QuickVerdict,
} from '../../../components/marketing/ArticleLayout';
import {
  ARTICLE_IMAGES,
  ARTICLE_SUMMARY_BY_SLUG,
} from '../../../lib/content/articles';

const article = ARTICLE_SUMMARY_BY_SLUG['how-much-can-i-spend-today'];

export default function HowMuchCanISpendTodayPage() {
  return (
    <ArticleLayout
      article={article}
      relatedSlugs={['your-daily-freedom-number', 'budgeting-from-take-home-pay-not-gross-fantasy']}
    >
      <QuickVerdict
        title="Quick answer"
        items={[
          'A useful daily spending number starts with money available for the month, then subtracts fixed bills, planned savings, required debt payments, and spending that already happened.',
          'For FIRE-minded households, the number should also respect the long-term plan instead of treating every dollar in checking as available.',
          'The goal is not to make spending feel tiny. It is to make the tradeoff visible before the purchase.',
        ]}
      />

      <ArticleSection title="Why your bank balance is the wrong starting point">
        <ArticleParagraph>
          The question "how much can I spend today?" sounds simple, but most financial
          dashboards make it hard to answer. A checking balance includes money that is
          already spoken for by rent, mortgage payments, credit card autopays, subscriptions,
          tuition, taxes, transfers, and future groceries. It also ignores the savings rate
          or FIRE contribution target that made the plan work in the first place.
        </ArticleParagraph>
        <ArticleParagraph>
          That is why a daily spending number should not be a vibe check against the
          account balance. It should be a small cash-flow model: what came in, what must
          still go out, what has already been spent, and how many days are left before
          the next reset.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileBudget}
        alt="Per Diem mobile budget screen with category planning"
        caption="A believable safe-to-spend number starts with the current budget, bills, savings targets, and spending that already happened."
        variant="phone"
      />

      <ArticleSection title="A simple safe-to-spend formula">
        <ArticleParagraph>
          A manual version of the calculation can be written like this: start with
          expected take-home income for the month, subtract fixed bills, subtract planned
          savings and investing, subtract planned non-monthly expenses, subtract spending
          that already happened, then divide the remaining amount by the days left.
        </ArticleParagraph>
        <ArticleBullets
          items={[
            'Use take-home income, not gross salary. Payroll deductions and withholding are not spendable cash.',
            'Separate fixed commitments from flexible spending. Rent, insurance, subscriptions, and required debt payments should leave the daily pool before you start dividing.',
            'Treat savings as a bill if the FIRE plan depends on it. Otherwise the daily number will quietly steal from the future.',
            'Update the number after real spending posts. Overspending should tighten the rest of the month; underspending should give the household more room.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="Where FIRE changes the calculation">
        <ArticleParagraph>
          A normal daily budget asks whether the month still works. A FIRE-aware daily
          budget asks whether the month still works without nudging the long-term plan
          off course. That is a different bar.
        </ArticleParagraph>
        <ArticleParagraph>
          For a household pursuing financial independence, daily spending is not just
          about avoiding overdrafts or staying within category limits. It affects savings
          rate, taxable brokerage contributions, cash reserves, planned account draws,
          and the time it may take to reach FI. The daily number becomes more useful when
          it is connected to the same assumptions behind the retirement model.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="The number should move when real life moves">
        <ArticleParagraph>
          Static budgets break because life does not spend in perfect daily slices. A
          good daily spending number adapts when groceries come in high, a refund lands,
          a bill moves, or a planned purchase becomes real.
        </ArticleParagraph>
        <ArticleBullets
          items={[
            'If you underspend today, the unused room can roll forward instead of disappearing.',
            'If you overspend today, the remaining days should adjust without requiring a full budget rebuild.',
            'If a large life event is planned, the daily number should reflect that cash need before the month gets tight.',
            'If the FIRE plan changes, the current month should inherit that context instead of staying detached.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="What Per Diem does differently">
        <ArticleParagraph>
          Per Diem is built around this question. The app connects account balances,
          transactions, budgets, income, portfolio context, and FIRE planning into one
          household model, then turns that model into a daily spending number.
        </ArticleParagraph>
        <ArticleParagraph>
          The point is not to make every purchase feel heavy. It is to replace the
          guessing game with a practical signal: what can today handle while the broader
          plan stays intact?
        </ArticleParagraph>
      </ArticleSection>
    </ArticleLayout>
  );
}
